import React, { PureComponent } from 'react';
import { Marker } from 'react-google-maps';
import {
  Button,
  Header,
  Segment,
  Message,
  Modal,
  Card,
  Icon,
  Checkbox,
} from 'semantic-ui-react';
import clone from 'lodash/clone';
import findIndex from 'lodash/findIndex';
import flatten from 'lodash/flatten';
import chunk from 'lodash/chunk';
import set from 'lodash/set';
import uniq from 'lodash/uniq';
import debounce from 'lodash/debounce';
import keyBy from 'lodash/keyBy';
import uniqWith from 'lodash/uniqWith';
import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';
import last from 'lodash/last';
import difference from 'lodash/difference';
import minBy from 'lodash/minBy';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import mean from 'lodash/mean';
import moment from 'moment-timezone';
import Chance from 'chance';
import Promise from 'bluebird';
import memoize from 'memoize-one';
import geolib from 'geolib';

import Map from '../../../components/common/Map';
import PlannerVehiclesManager from '../../../components/planner/PlannerVehiclesManager';
import TravelRequirementsManager from '../../../components/planner/TravelRequirementsManager';
import TravelRequirementEditor from '../../../components/planner/TravelRequirementEditor';
import RouteDisplay from '../../../components/planner/RouteDisplay';
import RoutesSaver from '../../../components/planner/RoutesSaver';
import RoutesImporter from '../../../components/planner/RoutesImporter';
import PlannerOptions from '../../../components/planner/PlannerOptions';
import VehiclesMap from '../../../components/liveMap/VehiclesMap';
import { routesToTrips } from '../../../components/planner/RoutesSaver/RoutesSaver';

const busIconPath = `M219.5,0 C332.994516,-2.08486044e-14 425,92.0054839 425,205.5 C425,303.170707 356.861492,384.926812 265.52714,405.825653 L225.789848,462.622694 C221.040945,469.410703 211.688429,471.063725 204.90042,466.314821 C203.505776,465.339126 202.287366,464.133183 201.297378,462.748648 L160.803131,406.12537 C68.8009169,385.719308 1.20192117e-14,303.644312 0,205.5 C-1.38990696e-14,92.0054839 92.0054839,2.08486044e-14 205.5,0 L219.5,0 Z M250.500007,309.5 L175.500007,309.5 L175.500007,334.5 C175.500007,341.403559 169.903573,347 163.000007,347 L150.500007,347 C143.596454,347 138.000007,341.403559 138.000007,334.5 L138.000007,302.8125 C122.522705,293.882634 112.990838,277.368673 113.000007,259.5 L113.000007,147 C113.000007,119.385763 135.385776,97 163.000007,97 L263.000007,97 C290.614251,97 313.000007,119.385763 313.000007,147 L313.000007,259.5 C313.000007,278 302.950013,294.1625 288.000007,302.8125 L288.000007,334.5 C288.000007,341.403559 282.403573,347 275.500007,347 L263.000007,347 C256.096454,347 250.500007,341.403559 250.500007,334.5 L250.500007,309.5 Z M288.000013,209.5 L288.000013,147 C288.000013,133.192881 276.807132,122 263.000013,122 L163.000013,122 C149.192894,122 138.000013,133.192881 138.000013,147 L138.000013,209.5 L288.000013,209.5 Z M288.000013,234.5 L138.000013,234.5 L138.000013,259.5 C138.000013,273.307119 149.192894,284.5 163.000013,284.5 L263.000013,284.5 C276.807132,284.5 288.000013,273.307119 288.000013,259.5 L288.000013,234.5 Z M163.000013,134.5 C169.903573,134.5 175.500013,140.096441 175.500013,147 L175.500013,184.5 C175.500013,191.403559 169.903573,197 163.000013,197 C156.096454,197 150.500013,191.403559 150.500013,184.5 L150.500013,147 C150.500013,140.096441 156.096454,134.5 163.000013,134.5 Z M163.000013,272 C156.096454,272 150.500013,266.403559 150.500013,259.5 C150.500013,252.596441 156.096454,247 163.000013,247 C169.903573,247 175.500013,252.596441 175.500013,259.5 C175.500013,266.403559 169.903573,272 163.000013,272 Z M263.000013,272 C256.096454,272 250.500013,266.403559 250.500013,259.5 C250.500013,252.596441 256.096454,247 263.000013,247 C269.903573,247 275.500013,252.596441 275.500013,259.5 C275.500013,266.403559 269.903573,272 263.000013,272 Z`;
const BUS_AVERAGE_SPEED = 5.677408; // m/s

const chance = new Chance();
const mapRef = React.createRef();

class AutomaticPlannerScreen extends PureComponent {
  state = {
    depot: null,
    travelRequirements: [],
    selectedRequirement: null,
    addingTravelRequirement: false,
    mapCenter: null,
    vehicles: [],
    generating: false,
    routes: [],
    selectedDay: 1,
    focusedRoute: null,
    error: null,
    saveModalOpen: false,
    importModalOpen: false,
    useRoutesAsInitial: false,
    unfulfilledStops: [],
    mapViewMode: 'DEMANDS',
    includePlannedDemands: false,
    options: {
      selectedDay: 1,
      timeMapKey: '',
      timeMultiplier: 1,
      maxTimeMultiplier: 10,
      overtimePenaltyCoef: 1,
      overtimePenalty: 1,
      departureTime: null,
      distanceCostCoefficient: 1,
      ignoreVehicleDepots: false,
      splitMultiPassengerDemands: false,
      selectedTimeRange: [0, 15],
      maxTime: 5400,
      maxTimeSoft: 2700,
      overdistancePenaltyCoef: 1,
      overdistancePenalty: 1,
      timeLimit: 120,
    },
  };

  componentDidMount() {
    document.title = 'Planificateur';
  }

  updateVehicles = vehicles => {
    this.setState({
      vehicles,
    });
  };

  updateTravelRequirements = travelRequirements => {
    this.setState({
      travelRequirements,
    });
  };

  selectRequirement = requirement => {
    this.setState({
      selectedRequirement: requirement,
      mapViewMode: 'DEMANDS',
    });
  };

  dismissSelectedRequirement = () => {
    this.setState({
      selectedRequirement: null,
    });
  };

  updateSelectedRequirement = updatedRequirement => {
    this.setState({
      travelRequirements: this.state.travelRequirements.map(r =>
        r.id === updatedRequirement.id ? updatedRequirement : r,
      ),
    });
  };

  focusRoute = route => {
    const { focusTrips } = this.props;
    this.setState({
      focusedRoute: route,
      mapViewMode: 'TRIPS',
    });
    focusTrips(routesToTrips([route]), true);
  };

  dismissFocusedRoute = () => {
    this.setState({
      focusedRoute: null,
    });
  };

  vehiclesById = memoize(vehicles => keyBy(vehicles, 'id'));

  importRoutes = routes => {
    this.setState({
      routes: routes.map(route => ({
        ...route,
        color: this.vehiclesById(this.state.vehicles)[route.vehicle.id].color,
      })),
      useRoutesAsInitial: true,
      importModalOpen: false,
    });
  };

  getDistanceMatrix = async options => {
    console.log(
      `Getting distance matrix between ${options.origins.length} and ${options.destinations.length} stops`,
    );
    let { travelRequirements, departureTime } = options;
    if (!departureTime) {
      // If the user did not select a departure time
      // we take the mean of all demand times
      departureTime = moment.unix(
        mean(
          travelRequirements.map(requirement =>
            requirement.departure.time || requirement.arrival.time
              ? moment(requirement.departure.time || requirement.arrival.time)
                  .set('day', this.state.options.selectedDay)
                  .unix()
              : null,
          ),
        ),
      );

      // In case the selected day for this week has already passed, we
      // use the same day but next week
      if (departureTime.isBefore(moment())) {
        departureTime.add(1, 'week');
      }
    }

    const response = await fetch(
      `https://api.tomtom.com/routing/1/matrix/json?key=ZyHeo23dxQWcEATKYqz10Vx14qDt6Jjl&redirectMode=manual&traffic=true&departAt=${departureTime.toISOString()}&travelMode=car`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          origins: options.origins.map(latLng => ({
            point: latLngToCoordinates(latLng),
          })),
          destinations: options.destinations.map(latLng => ({
            point: latLngToCoordinates(latLng),
          })),
        }),
      },
    );
    if (!response.ok) {
      if (response.body) {
        const body = await response.json();
        throw new Error(
          body.message ||
            'Unknown error when fetching distance matrix response',
        );
      } else {
        throw new Error('Network error');
      }
    }
    if (response.status === 202) {
      while (true) {
        const matrixLocation = response.headers.get('Location');
        const matrixLocationResponse = await fetch(
          `https://api.tomtom.com${matrixLocation}`,
        );
        if (!matrixLocationResponse.ok) {
          if (matrixLocationResponse.body) {
            const body = await matrixLocationResponse.json();
            throw new Error(
              body.message ||
                'Unknown error when fetching distance matrix response',
            );
          } else {
            throw new Error('Network error');
          }
        }
        if (matrixLocationResponse.status === 202) {
          continue;
        }
        const matrixBody = await matrixLocationResponse.json();
        return matrixBody;
      }
    } else {
      const body = await response.json();
      return body;
    }
  };

  resetTmCache = () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log(key);
      if (key.startsWith('PLANNER::TIME_MAP::')) {
        keysToRemove.push(key);
      }
    }
    for (let key of keysToRemove) {
      localStorage.removeItem(key);
    }
  };

  generateRoutes = async () => {
    try {
      let {
        options,
        travelRequirements,
        vehicles,
        depot,
        routes,
        useRoutesAsInitial,
      } = this.state;
      this.setState({
        generating: true,
        routes: [],
        unfulfilledStops: [],
        unfulfilledDemandIds: [],
      });
      const {
        timeMultiplier,
        selectedDay,
        maxTimeMultiplier,
        overtimePenaltyCoef,
        overtimePenalty,
        overdistancePenalty,
        overdistancePenaltyCoef,
        distanceCostCoefficient,
        ignoreVehicleDepots,
        timeMapKey,
        timeLimit,
        maxTime,
        maxTimeSoft,
      } = options;

      vehicles = vehicles.filter(v => !v.disabled);

      const defaultDepot = {
        name: 'Dépôt par défaut',
        coords: depot.coords,
      };

      const requirementGroups = groupBy(
        travelRequirements,
        r => r.departure.time || r.arrival.time,
      );

      const mergedGroups = reduce(
        requirementGroups,
        (accumulator, group) => {
          const lastGroup = last(accumulator);
          if (
            lastGroup &&
            lastGroup.length < 500 &&
            group.length < 500 &&
            lastGroup.length + group.length < 500
          ) {
            lastGroup.push(...group);
            return accumulator;
          } else {
            accumulator.push(group);
            return accumulator;
          }
        },
        [],
      );

      const stopDistance = (stop1, stop2) =>
        geolib.getDistance(
          { latitude: stop1.coords.lat, longitude: stop1.coords.lng },
          { latitude: stop2.coords.lat, longitude: stop2.coords.lng },
        );

      await Promise.map(mergedGroups, async travelRequirementsGroup => {
        const depots = vehicles.map(vehicle => vehicle.depot || defaultDepot);

        let allStops = uniqWith(
          uniq(depots).concat(
            flatten(
              travelRequirementsGroup.map(requirement => [
                requirement.departure,
                requirement.arrival,
              ]),
            ),
          ),
          (stop1, stop2) => {
            if (stopDistance(stop1, stop2) < 300) {
              return true;
            }
          },
        );

        let timeMapCoords = null;

        const timeMap =
          timeMapKey && (await readStorage(timeMapKey, 'utf-8'))
            ? JSON.parse(await readStorage(timeMapKey, 'utf-8'))
            : {};
        const distanceMap =
          timeMapKey && (await readStorage(timeMapKey + '-distance', 'utf-8'))
            ? JSON.parse(await readStorage(timeMapKey + '-distance', 'utf-8'))
            : {};

        console.log('timeMapKey', timeMapKey);
        debugger;
        if (!isEmpty(timeMap)) {
          timeMapCoords = {};
          Object.keys(timeMap).forEach(key => {
            const originCoordsString = key.split('->')[0];
            const destCoordsString = key.split('->')[1];
            timeMapCoords[originCoordsString] = 1;
            timeMapCoords[destCoordsString] = 1;
          });
        }

        allStops = allStops.filter(stop => {
          // Only keep the stops that are already in the timemap
          if (isEmpty(timeMap)) {
            return true;
          }
          return timeMapCoords[`${stop.coords.lat},${stop.coords.lng}`];
        });

        console.log(
          'Calculating time matrix for',
          allStops.length,
          'stops',
          allStops,
        );

        // Distance matrix does not support more than 100 elements
        // per request
        const allStopsChunks = chunk(allStops, 25);

        if (isEmpty(timeMap)) {
          await Promise.map(
            allStopsChunks,
            async chunk => {
              await Promise.map(
                allStopsChunks,
                async otherChunk => {
                  const distanceMatrixResponse = await this.getDistanceMatrix({
                    origins: chunk.map(stop => stop.coords),
                    destinations: otherChunk.map(stop => stop.coords),
                    travelMode: 'DRIVING',
                    travelRequirements,
                    departureTime: moment(options.departureTime).toDate(),
                  });
                  console.log('Got dm response', distanceMatrixResponse);
                  const localTimeMatrix = distanceMatrixResponse.matrix.map(
                    row =>
                      row.map(
                        element =>
                          element.response.routeSummary.travelTimeInSeconds,
                      ),
                  );
                  const localDistanceMatrix = distanceMatrixResponse.matrix.map(
                    row =>
                      row.map(
                        element => element.response.routeSummary.lengthInMeters,
                      ),
                  );
                  localTimeMatrix.forEach((row, rowIndex) => {
                    row.forEach((timeValue, columnIndex) => {
                      const originStop = chunk[rowIndex];
                      const destStop = otherChunk[columnIndex];
                      set(
                        timeMap,
                        [
                          `${originStop.coords.lat},${originStop.coords.lng}->${destStop.coords.lat},${destStop.coords.lng}`,
                        ],
                        timeValue,
                      );
                      set(
                        distanceMap,
                        [
                          `${originStop.coords.lat},${originStop.coords.lng}->${destStop.coords.lat},${destStop.coords.lng}`,
                        ],
                        localDistanceMatrix[rowIndex][columnIndex],
                      );
                    });
                  });
                },
                {
                  concurrency: 5,
                },
              );
            },
            {
              concurrency: 1,
            },
          );
        }

        await writeStorage(timeMapKey, JSON.stringify(timeMap));
        await writeStorage(
          timeMapKey + '-distance',
          JSON.stringify(distanceMap),
        );

        const dayTravelRequirements = travelRequirementsGroup;
        if (!dayTravelRequirements.length) {
          return;
        }
        const dayAllStops = uniq(depots).concat(
          flatten(
            dayTravelRequirements.map(requirement => [
              requirement.departure,
              requirement.arrival,
            ]),
          ),
        );
        // .concat(
        //   flatten(vehicles.map((vehicle) => [
        //     {
        //       name: 'RESET_DISTANCE',
        //       type: 'RESET_DISTANCE',
        //       coords: {
        //         lat: 15,
        //         lng: -15,
        //       },
        //     },
        //     {
        //       name: 'RESET_DISTANCE',
        //       type: 'RESET_DISTANCE',
        //       coords: {
        //         lat: 15,
        //         lng: -15,
        //       },
        //     },
        //     {
        //       name: 'RESET_DISTANCE',
        //       type: 'RESET_DISTANCE',
        //       coords: {
        //         lat: 15,
        //         lng: -15,
        //       },
        //     },
        //   ])),
        // );

        let initialRoutes = null;
        if (useRoutesAsInitial) {
          const dayRoutes = routes;
          const routesByVehicleId = keyBy(dayRoutes, 'vehicle.id');
          initialRoutes = vehicles
            .map(vehicle => {
              const assignedRoute = routesByVehicleId[vehicle.id];
              if (assignedRoute) {
                const route = assignedRoute.stops
                  .map(stop => {
                    if (!stop.id) {
                      return null;
                    }
                    const stopIndex = findIndex(
                      dayAllStops,
                      s => s.id === stop.id,
                    );
                    return stopIndex !== -1 ? stopIndex : null;
                  })
                  .filter(v => v !== null);
                return route;
              } else {
                return [];
              }
            })
            .filter(v => v !== null);
        }

        const timeMatrix = dayAllStops.map(originStop =>
          dayAllStops.map(destStop => {
            if (
              ignoreVehicleDepots &&
              (depots.includes(originStop) || depots.includes(destStop))
            ) {
              return 0;
            }
            if (isEqual(originStop.coords, destStop.coords)) {
              return 0;
            }
            // if (originStop.type === 'RESET_DISTANCE' || destStop.type === 'RESET_DISTANCE') {
            //   return 0;
            // }

            // const time =
            //   geolib.getDistance(
            //     latLngToCoordinates(originStop.coords),
            //     latLngToCoordinates(destStop.coords),
            //   ) / BUS_AVERAGE_SPEED;
            // if (depots.includes(originStop) || depots.includes(destStop)) {
            //   return time * 0.01;
            // }
            // return time;
            const closestStopToOrigin = minBy(allStops, stop =>
              geolib.getDistance(
                latLngToCoordinates(originStop.coords),
                latLngToCoordinates(stop.coords),
              ),
            );
            const isCloserToOrigin =
              geolib.getDistance(
                latLngToCoordinates(originStop.coords),
                latLngToCoordinates(destStop.coords),
              ) <
              geolib.getDistance(
                latLngToCoordinates(closestStopToOrigin.coords),
                latLngToCoordinates(destStop.coords),
              )
                ? true
                : false;
            const closestStopToDest = minBy(allStops, stop =>
              geolib.getDistance(
                latLngToCoordinates(destStop.coords),
                latLngToCoordinates(stop.coords),
              ),
            );
            const isCloserToDest =
              geolib.getDistance(
                latLngToCoordinates(destStop.coords),
                latLngToCoordinates(originStop.coords),
              ) <
              geolib.getDistance(
                latLngToCoordinates(closestStopToDest.coords),
                latLngToCoordinates(originStop.coords),
              )
                ? true
                : false;
            const timeMapKey = `${closestStopToOrigin.coords.lat},${closestStopToOrigin.coords.lng}->${closestStopToDest.coords.lat},${closestStopToDest.coords.lng}`;
            const time = timeMap[timeMapKey];
            const timeToClosestStopToOrigin =
              (geolib.getDistance(
                latLngToCoordinates(originStop.coords),
                latLngToCoordinates(closestStopToOrigin.coords),
              ) /
                BUS_AVERAGE_SPEED) *
              (isCloserToOrigin ? -0.5 : 1);
            const timeToClosestStopToDest =
              (geolib.getDistance(
                latLngToCoordinates(destStop.coords),
                latLngToCoordinates(closestStopToDest.coords),
              ) /
                BUS_AVERAGE_SPEED) *
              (isCloserToDest ? -0.5 : 1);

            const isFromSite = originStop.time && !destStop.time;
            const timePenalty = isFromSite ? 0 : 0;
            console.log(
              'Distance between',
              originStop.name,
              'and',
              destStop.name,
              'determined by',
              closestStopToOrigin.name,
              'and',
              closestStopToDest.name,
              'time',
              time,
              'ttco',
              timeToClosestStopToOrigin,
              'ttcd',
              timeToClosestStopToDest,
            );
            const adjustedTime =
              Math.max(
                Math.round(
                  (time + timeToClosestStopToOrigin + timeToClosestStopToDest) *
                    (timeMultiplier ? parseFloat(timeMultiplier) : 1),
                ),
                120,
              ) + timePenalty;
            return adjustedTime;
          }),
        );
        const distanceMatrix = dayAllStops.map(originStop =>
          dayAllStops.map(destStop => {
            if (
              ignoreVehicleDepots &&
              (depots.includes(originStop) || depots.includes(destStop))
            ) {
              return 0;
            }
            if (isEqual(originStop.coords, destStop.coords)) {
              return 0;
            }
            // if (originStop.type === 'RESET_DISTANCE' && destStop.type === 'RESET_DISTANCE') {
            //   return 0;
            // }
            // if (originStop.type === 'RESET_DISTANCE') {
            //   return 0;
            // }
            // if (originStop.time && originStop.demand < 0 && destStop.type === 'RESET_DISTANCE') {
            //   return -1000000;
            // }
            // if (destStop.type === 'RESET_DISTANCE') {
            //   return 0;
            // }
            // const distance = geolib.getDistance(latLngToCoordinates(originStop.coords), latLngToCoordinates(destStop.coords));
            // if (depots.includes(originStop) || depots.includes(destStop)) {
            //   return distance;
            // }
            // return distance;
            const closestStopToOrigin = minBy(allStops, stop =>
              geolib.getDistance(
                latLngToCoordinates(originStop.coords),
                latLngToCoordinates(stop.coords),
              ),
            );
            const closestStopToDest = minBy(allStops, stop =>
              geolib.getDistance(
                latLngToCoordinates(destStop.coords),
                latLngToCoordinates(stop.coords),
              ),
            );
            const distanceMapKey = `${closestStopToOrigin.coords.lat},${closestStopToOrigin.coords.lng}->${closestStopToDest.coords.lat},${closestStopToDest.coords.lng}`;
            const distance = distanceMap[distanceMapKey];
            const distanceToClosestStopToOrigin = geolib.getDistance(
              latLngToCoordinates(originStop.coords),
              latLngToCoordinates(closestStopToOrigin.coords),
            );
            const distanceToClosestStopToDest = geolib.getDistance(
              latLngToCoordinates(destStop.coords),
              latLngToCoordinates(closestStopToDest.coords),
            );
            const adjustedDistance = Math.round(
              distance +
                distanceToClosestStopToOrigin +
                distanceToClosestStopToDest,
            );
            if (depots.includes(originStop) || depots.includes(destStop)) {
              return Math.round(adjustedDistance * 0.01);
            }
            return adjustedDistance;
          }),
        );

        const startOfDay = moment().startOf('day');
        const startOfDayUnix = startOfDay.unix();

        const makeStopTimeWindow = (stop, isReturn) =>
          stop.time
            ? isReturn
              ? [
                  moment(stop.time).unix() - startOfDayUnix,
                  moment(stop.time).add(10, 'minutes').unix() - startOfDayUnix,
                ]
              : [
                  moment(stop.time).subtract(20, 'minutes').unix() -
                    startOfDayUnix,
                  moment(stop.time).add(0, 'minutes').unix() - startOfDayUnix,
                ]
            : null;

        const timeWindows = uniq(depots)
          .map(makeStopTimeWindow)
          .concat(
            flatten(
              dayTravelRequirements.map(requirement => {
                const { departure, arrival } = requirement;
                if (arrival.time && !departure.time) {
                  const arrivalTimeWindow = makeStopTimeWindow(arrival, false);
                  return [null, arrivalTimeWindow];
                }
                if (departure.time && !arrival.time) {
                  const departureTimeWindow = makeStopTimeWindow(
                    departure,
                    true,
                  );
                  return [departureTimeWindow, null];
                }
                return [
                  makeStopTimeWindow(departure, false),
                  makeStopTimeWindow(arrival, false),
                ];
              }),
            ),
          );
        const pickupDeliveries = dayTravelRequirements.map(requirement => [
          dayAllStops.indexOf(requirement.departure),
          dayAllStops.indexOf(requirement.arrival),
        ]);

        const detours = new Array(dayAllStops.length)
          .fill(null)
          .map(v => new Array(dayAllStops.length).fill(0));

        // const angleDistance = (a, b) => {
        //   const diff = Math.abs(a - b) % 360;
        //   return diff > 180 ? 360 - diff : diff;
        // };

        // pickupDeliveries.forEach(pd => {
        //   const pickupStop = dayAllStops[pd[0]];
        //   const deliveryStop = dayAllStops[pd[1]];
        //   const pdBearing = geolib.getRhumbLineBearing(latLngToCoordinates(pickupStop.coords), latLngToCoordinates(deliveryStop.coords))
        //   dayAllStops.forEach((stop, index) => {
        //     const currentBearing = geolib.getRhumbLineBearing(latLngToCoordinates(pickupStop.coords), latLngToCoordinates(stop.coords));
        //     const stopBearingDiff = angleDistance(pdBearing, currentBearing)

        //     const stopPd = pickupDeliveries.find(pd => pd[0] === index || pd[1] === index);
        //     let stopPdBearing = 0;
        //     if (stopPd) {
        //       stopPdPickupStop = dayAllStops[stopPd[0]]
        //       stopPdDeliveryStop = dayAllStops[stopPd[1]]
        //       stopPdBearing = geolib.getRhumbLineBearing(latLngToCoordinates(stopPdPickupStop.coords), latLngToCoordinates(stopPdDeliveryStop.coords))
        //     }
        //     let stopPdBearingDiff = 0;
        //     if (stopPdBearing) {
        //      stopPdBearingDiff = angleDistance(pdBearing, stopPdBearing);
        //     }

        //     if (distanceMatrix[pd[0]][index] > 500) {
        //       distanceMatrix[pd[0]][index] = distanceMatrix[pd[0]][index] + distanceMatrix[pd[0]][index] * ((stopBearingDiff + stopPdBearingDiff) / 90);
        //       if (!stop.time) {
        //         distanceMatrix[pd[1]][index] = distanceMatrix[pd[1]][index] + distanceMatrix[pd[1]][index] * ((stopBearingDiff + stopPdBearingDiff) / 90);
        //       }
        //     }
        //   });
        // })

        const dropPenalty = new Array(dayAllStops.length);
        dropPenalty.fill(400 * 1000 * 1000);
        const demands = dayAllStops.map(stop => stop.demand || 0);
        const numVehicles = vehicles.length;
        const vehicleCapacities = vehicles.map(vehicle => vehicle.capacity);
        const vehicleCosts = vehicles.map(
          vehicle => parseInt(vehicle.cost) || 1,
        );
        const starts = vehicles.map(vehicle =>
          vehicle.depot
            ? dayAllStops.indexOf(vehicle.depot)
            : dayAllStops.indexOf(defaultDepot),
        );
        const ends = starts;

        const requestData = {
          time_matrix: timeMatrix,
          distance_matrix: distanceMatrix,
          detours,
          time_windows: timeWindows,
          pickups_deliveries: pickupDeliveries,
          drop_penalty: dropPenalty,
          split_stop_penalty: 50 * 1000 * 1,
          demands: demands,
          num_vehicles: numVehicles,
          vehicle_capacities: vehicleCapacities,
          vehicle_costs: vehicleCosts,
          starts,
          ends,
          initial_routes: initialRoutes,
          stops: dayAllStops,
          max_time_multiplier: parseFloat(maxTimeMultiplier),
          max_time: parseInt(maxTime),
          max_time_soft: parseInt(maxTimeSoft),
          overtime_penalty_coef: parseFloat(overtimePenaltyCoef),
          overdistance_penalty_coef: parseFloat(overdistancePenaltyCoef),
          overdistance_penalty: parseFloat(overdistancePenalty),
          overtime_penalty: parseFloat(overtimePenalty),
          distance_cost_coefficient: parseFloat(distanceCostCoefficient),
          time_limit: parseInt(timeLimit),
        };
        const response = await fetch(
          process.env.REACT_APP_SUNUBUS_SERVICES_URL +
            '/planner/routes/generate',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
          },
        );
        if (!response.ok) {
          const error = response.body
            ? await response.json()
            : { message: 'Network error' };
          throw error;
        }

        const data = await response.json();

        const parsedRoutes = data.map((route, index) => {
          return {
            id: chance.guid(),
            stops: route.map((stop, index) => ({
              ...stop,
              ...dayAllStops[stop.index],
              minDate: moment()
                .set('day', selectedDay)
                .startOf('day')
                .add(stop.min_time, 'seconds')
                .toDate(),
              maxDate: moment()
                .set('day', selectedDay)
                .startOf('day')
                .add(stop.max_time, 'seconds')
                .toDate(),
              distanceTo:
                index === 0
                  ? 0
                  : distanceMatrix[stop.index][route[index - 1].index],
            })),
            totalTime: route.reduce(
              (totalTime, stop) => totalTime + stop.min_time,
              0,
            ),
            vehicle: vehicles[index],
            color: vehicles[index].color,
          };
        });

        const fulfilledStops = flatten(parsedRoutes.map(route => route.stops));
        const fulfilledStopIds = fulfilledStops.map(stop => stop.id);
        const allStopIds = dayAllStops.map(stop => stop.id);
        const unfulfilledStopIds = difference(allStopIds, fulfilledStopIds);

        const unfulfilledStops = unfulfilledStopIds.map(id =>
          dayAllStops.find(s => s.id === id),
        );

        const unfulfilledDemandIds = flatten(
          unfulfilledStops.map(stop => stop.demandIds),
        );

        if (unfulfilledDemandIds.length > 0) {
          alert(
            'Il y a des arrêts non servis, veuillez consulter la liste des demandes',
          );
        }

        this.setState({
          routes: this.state.routes.concat(parsedRoutes),
          unfulfilledStops: this.state.unfulfilledStops.concat(
            unfulfilledStops,
          ),
          unfulfilledDemandIds,
          timeMap,
          timeMatrix,
          distanceMap,
          distanceMatrix,
        });

        const firstUsefulRoute = parsedRoutes.filter(
          route => route.stops.length > 2,
        )[0];
        if (firstUsefulRoute) {
          this.focusRoute(firstUsefulRoute);
        }
      });
    } catch (error) {
      console.error(error);
      this.setState({
        error,
      });
      return;
    } finally {
      this.setState({
        generating: false,
      });
    }
  };

  openSaveModal = () => {
    this.setState({
      saveModalOpen: true,
    });
  };

  closeSaveModal = () => {
    this.setState({
      saveModalOpen: false,
    });
  };

  openImportModal = () => {
    this.setState({
      importModalOpen: true,
    });
  };

  closeImportModal = () => {
    this.setState({
      importModalOpen: false,
    });
  };

  importGeneratedRoutes = async () => {
    console.log(this.state.routes);
  };

  updateDepot = updates => {
    const { depot } = this.state;
    this.setState({
      depot: {
        ...depot,
        ...updates,
      },
    });
  };

  onCenterChanged = debounce(latLng => {
    this.setState({
      mapCenter: latLng,
    });
    const { depot } = this.state;
    if (!depot) {
      this.setState({
        depot: {
          name: 'Dépôt',
          coords: clone(latLng),
        },
      });
    }
  }, 500);

  render() {
    const {
      depot,
      travelRequirements,
      selectedRequirement,
      mapCenter,
      generating,
      routes,
      focusedRoute,
      vehicles,
      error,
      saveModalOpen,
      importModalOpen,
      splitMultiPassengerDemands,
      unfulfilledDemandIds,
      options,
      mapViewMode,
      includePlannedDemands,
    } = this.state;
    return (
      <div style={styles.container}>
        <Segment basic style={styles.controlsContainer}>
          <Header style={styles.screenTitle} as="h1">
            Planification des trajets
          </Header>
          {error && (
            <Message error>
              <Message.Header>Une erreur est survenue</Message.Header>
              <Message.Content>
                {error.message || 'Erreur inconnue'}
              </Message.Content>
            </Message>
          )}
          <div style={styles.optionsVehiclesContainer}>
            <Card style={styles.optionsCard}>
              <Card.Content header="Options" />
              <Card.Content>
                <PlannerOptions
                  options={options}
                  onChange={options => {
                    this.setState({
                      options,
                    });
                  }}
                  onTmCacheReset={this.resetTmCache}
                  legacy={true}
                />
              </Card.Content>
            </Card>

            <PlannerVehiclesManager
              focusedVehicle={focusedRoute ? focusedRoute.vehicle : null}
              style={styles.vehiclesManager}
              onChange={this.updateVehicles}
            />
          </div>
          <Card
            style={{
              ...styles.requirementsManagerContainer,
              ...(mapViewMode === 'DEMANDS' &&
                styles.expandedRequirementsManagerContainer),
            }}
          >
            <Card.Content
              header={
                <div style={styles.demandsCardHeader}>
                  <Header>Demandes</Header>
                  <Checkbox
                    checked={includePlannedDemands}
                    label="Inclure les demandes planifiées"
                    onChange={(e, { checked }) =>
                      this.setState({
                        includePlannedDemands: checked,
                      })
                    }
                  />
                </div>
              }
              onClick={() =>
                this.setState({
                  mapViewMode: 'DEMANDS',
                })
              }
            />
            {mapViewMode === 'DEMANDS' && (
              <Card.Content style={styles.requirementsManagerCardContent}>
                <TravelRequirementsManager
                  splitMultiPassengerDemands={splitMultiPassengerDemands}
                  selectedDay={options.selectedDay}
                  selectedGroups={options.selectedGroups}
                  selectedTimeRange={options.selectedTimeRange}
                  droppedDemandIds={unfulfilledDemandIds}
                  mapCenter={mapCenter}
                  style={styles.requirementsManager}
                  onChange={this.updateTravelRequirements}
                  onSelect={this.selectRequirement}
                  includePlanned={includePlannedDemands}
                />
              </Card.Content>
            )}
            {mapViewMode === 'DEMANDS' && (
              <Card.Content textAlign="right">
                <Button
                  primary
                  onClick={this.generateRoutes}
                  disabled={generating || !travelRequirements.length}
                >
                  {generating ? 'Génération en cours' : 'Générer trajets'}
                </Button>
              </Card.Content>
            )}
          </Card>
          <div
            style={{
              ...styles.routesDisplaysContainer,
              ...(mapViewMode === 'TRIPS' &&
                styles.expandedRoutesDisplaysContainer),
            }}
          >
            <Card style={styles.tripsListCard}>
              <Card.Content
                header="Trajets"
                onClick={() => this.setState({ mapViewMode: 'TRIPS' })}
              />
              {mapViewMode === 'TRIPS' && (
                <Card.Content>
                  <Card.Description>
                    {routes
                      .filter(route => route.stops.length > 2)
                      .map((route, index) => (
                        <RouteDisplay
                          key={index}
                          route={route}
                          focused={focusedRoute && route.id === focusedRoute.id}
                          onFocus={this.focusRoute}
                        />
                      ))}
                  </Card.Description>
                </Card.Content>
              )}
            </Card>
          </div>
        </Segment>
        <Segment basic style={styles.routesContainer}>
          <div style={styles.routesActions}>
            <Button
              onClick={this.openImportModal}
              disabled={generating}
              primary
              inverted
            >
              <Icon name="download" />
              Importer
            </Button>
            <Button
              primary
              onClick={this.openSaveModal}
              disabled={generating || !routes.length}
              loading={false}
            >
              <Icon name="save" />
              Enregistrer
            </Button>
          </div>
          <div style={styles.mapContainer}>
            {mapViewMode === 'DEMANDS' && (
              <Map
                style={styles.map}
                onCenterChanged={() => {
                  this.onCenterChanged(mapRef.current.getCenter().toJSON());
                }}
                onTilesLoaded={() =>
                  this.onCenterChanged(mapRef.current.getCenter().toJSON())
                }
                onClick={() => {
                  this.dismissSelectedRequirement();
                  this.dismissFocusedRoute();
                }}
                defaultZoom={14}
                ref={mapRef}
              >
                {travelRequirements.map((requirement, index) => (
                  <TravelRequirementEditor
                    key={index}
                    requirement={requirement}
                    readonly={
                      !selectedRequirement ||
                      requirement.id !== selectedRequirement.id
                    }
                    onChange={this.updateSelectedRequirement}
                    onSelect={this.selectRequirement}
                    onDismiss={this.dismissSelectedRequirement}
                  />
                ))}
                {depot && (
                  <Marker
                    position={depot.coords}
                    draggable={true}
                    label="Dépôt"
                    onDragEnd={event =>
                      this.updateDepot({ coords: event.latLng.toJSON() })
                    }
                  />
                )}
                {vehicles
                  .filter(vehicle => vehicle.available)
                  .filter(vehicle => vehicle.depot)
                  .map(vehicle => (
                    <Marker
                      key={vehicle.id}
                      position={vehicle.depot.coords}
                      icon={{
                        path: busIconPath,
                        fillColor: vehicle.color,
                        fillOpacity: 1,
                        strokeColor: 'white',
                        strokeWeight: 1,
                        strokeOpacity: 1,
                        scale: 0.1,
                        anchor: { x: 212.5, y: 470 },
                      }}
                    ></Marker>
                  ))}
              </Map>
            )}
            {mapViewMode === 'TRIPS' && <VehiclesMap />}
          </div>
        </Segment>
        <Modal open={saveModalOpen} onClose={this.closeSaveModal} size="large">
          <Header icon="browser" content="Enregistrer les trajets" />
          <Modal.Content>
            <RoutesSaver routes={routes} />
          </Modal.Content>
        </Modal>
        <Modal
          open={importModalOpen}
          onClose={this.closeImportModal}
          size="large"
        >
          <Header icon="browser" content="Importer/Exporter" />
          <Modal.Content>
            <RoutesImporter
              routes={routes}
              onImport={this.importRoutes}
              defaultDepot={depot}
            />
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}

const styles = {
  container: {
    height: 'calc(100% - 80px)',
    display: 'flex',
    flexDirection: 'row',
  },
  screenTitle: {
    paddingBottom: 10,
  },
  routesContainer: {
    flex: 2,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  routesActions: {
    alignSelf: 'flex-end',
    paddingBottom: 20,
  },
  mapContainer: {
    flex: 1,
  },
  routesDisplaysContainer: {
    overflow: 'auto',
    flex: 1,
  },
  expandedRoutesDisplaysContainer: {
    height: '40%',
  },
  controlsContainer: {
    height: '100%',
    overflow: 'auto',
    flex: 1,
    margin: 0,
  },
  optionsCard: {
    marginRight: 10,
  },
  optionsVehiclesContainer: {
    display: 'flex',
  },
  vehiclesManager: {
    // 115px = height of header + generate button + card margin
    height: 'calc((100% - 115px) / 2)',
    marginTop: 0,
  },
  demandsCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  requirementsManagerContainer: {
    width: '100%',
  },
  expandedRequirementsManagerContainer: {
    height: '40%',
  },
  requirementsManagerCardContent: {
    // 110px = height of header + footer
    height: 'calc(100% - 110px)',
  },
  requirementsManager: {
    height: '100%',
    overflow: 'auto',
  },
  tripsListCard: {
    width: '100%',
    margin: 0,
    cursor: 'pointer',
    marginTop: 10,
  },
};

export default AutomaticPlannerScreen;

const latLngToCoordinates = latLng => ({
  latitude: latLng.lat,
  longitude: latLng.lng,
});

async function readStorage(key) {
  return localStorage.getItem(key);
}

async function writeStorage(key, value) {
  localStorage.setItem(key, value);
}
